import { queryClient } from "App";
import tagConstants from "constants/Tag";
import { get } from "lodash";
import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  getCampaignTargetsTableColumns,
  getCampaignTargetsTableFilters,
} from "routes/campaign/components/campaign_target_table/CampaignTargetTableColumnsAndFilters";
import { CAMPAIGN_TARGET_TABLE_CONSTANTS } from "routes/campaign/components/campaign_target_table/CampaignTargetTableV3";
import CampaignService from "services/Campaign";
import EmailTemplateService from "services/EmailTemplate";
import RevenueOpportunityService from "services/RevenueOpportunities";
import TagService from "services/Tag";
import UserService from "services/User";
import CustomFieldService from "services/CustomField";
import { buildCustomFieldFilters } from "components/BaseTableV3/table-helpers";
import { addCustomFieldsColumns, addResizedColumns } from "./useTableConfig";
import { campaignTargetKeys } from "routes/campaign/components/campaign_target_table/queryKeys";

const fetchTags = async () => {
  const taggableModels = tagConstants.taggableModels;
  const model = Object.keys(taggableModels).find(
    key => taggableModels[key] === CAMPAIGN_TARGET_TABLE_CONSTANTS.className
  );

  const tags = await TagService.getTags({ model, for_filtering: true });

  return tags;
};
const fetchCampaignStatuses = async () => {
  const rawStatuses = await CampaignService.getCampaignStatuses();
  const campaignStatuses = rawStatuses.map(({ id, name }) => ({
    id,
    name,
  }));

  return campaignStatuses;
};

const fetchRevenueOpportunityStatuses = async () => {
  const revenueOpportunityStatuses = (
    await RevenueOpportunityService.getRevenueOpportunityStatusesForFilters()
  ).map(({ id, name }) => ({ id, name }));
  return revenueOpportunityStatuses;
};

const fetchTemplates = async () => {
  const rawTemplates = await EmailTemplateService.getEmailTemplatesForFilters();
  const templates = rawTemplates.map(template => ({
    id: template.id,
    name: template.name,
  }));
  return templates;
};

const fetchCustomFields = async campaignId => {
  const customFields =
    await CampaignService.getCampaignTargetsCustomFields(campaignId);
  const entitiesCustomFields =
    await CampaignService.getTargettedEntitiesCustomFields(campaignId);
  const targetConfigs =
    await CustomFieldService.getCustomFieldConfigsForModel("CampaignTarget");
  const entityConfigs =
    await CustomFieldService.getCustomFieldConfigsForModel("Entity");

  return [
    {
      accessorPrefix: "custom_fields",
      headerPrefix: "Campaign Target",
      fields: customFields,
      model: "CampaignTarget",
      configs: targetConfigs,
    },
    {
      accessorPrefix: "entity_custom_fields",
      headerPrefix: "Entity",
      fields: entitiesCustomFields,
      model: "Entity",
      configs: entityConfigs,
    },
  ];
};

export const useCampaignTargetTable = ({
  campaignId,
  campaign,
  tableName = CAMPAIGN_TARGET_TABLE_CONSTANTS.tableName,
}) => {
  const { data: campaignStatuses, isSuccess: isCampaignStatusesFetched } =
    useQuery({
      queryKey: ["campaign-statuses", tableName],
      queryFn: fetchCampaignStatuses,
    });
  const {
    data: revenueOpportunityStatuses,
    isSuccess: isRevenueOpportunityStatusesFetched,
  } = useQuery({
    queryKey: ["revenue-opportunity-statuses"],
    queryFn: fetchRevenueOpportunityStatuses,
  });
  const { data: templates, isSuccess: isTemplatesFetched } = useQuery({
    queryKey: ["templates", tableName],
    queryFn: fetchTemplates,
  });
  const { data: customFields, isSuccess: isCustomFieldsFetched } = useQuery({
    // Keyed on campaignId, not tableName: tableName is a constant here, so
    // every campaign shared one entry and each new campaign rendered the
    // previously loaded campaign's custom-field columns.
    queryKey: ["custom-fields", "campaign-target", campaignId],
    queryFn: () => fetchCustomFields(campaignId),
    enabled: !!campaignId,
  });
  const { data: tags, isSuccess: isTagsFetched } = useQuery({
    queryKey: ["tags", tableName],
    queryFn: fetchTags,
  });
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const queryKey = useMemo(
    () => campaignTargetKeys.list({ campaignId }),
    [campaignId]
  );

  const invalidateData = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey }),
      // The detail sidebar's entity and contact counts come from the target
      // list and stay mounted behind the tab.
      queryClient.invalidateQueries({ queryKey: ["campaign"] }),
    ]);
  }, [queryKey]);

  const deleteCampaignTarget = useCallback(
    async (campaignId, entityId) => {
      await CampaignService.deleteCampaignTarget(campaignId, entityId);
      await invalidateData();
    },
    [invalidateData]
  );

  const deleteManyCampaignTarget = useCallback(
    async (campaignId, entityIds = []) => {
      await CampaignService.deleteCampaignTargets(campaignId, entityIds);
      await invalidateData();
    },
    [invalidateData]
  );

  const filters = useMemo(() => {
    if (
      !isTagsFetched ||
      !isRevenueOpportunityStatusesFetched ||
      !isCampaignStatusesFetched ||
      !isTemplatesFetched ||
      !isCustomFieldsFetched
    ) {
      return [];
    }
    const finalFilters = getCampaignTargetsTableFilters({
      tags,
      campaignId,
      revenueOpportunityStatuses,
      campaignStatuses,
      templates,
    });
    finalFilters.push(
      ...buildCustomFieldFilters(
        customFields,
        finalFilters.map(f => f.title)
      )
    );

    return finalFilters;
  }, [
    campaignId,
    tags,
    revenueOpportunityStatuses,
    campaignStatuses,
    templates,
    customFields,
    isTagsFetched,
    isRevenueOpportunityStatusesFetched,
    isCampaignStatusesFetched,
    isTemplatesFetched,
    isCustomFieldsFetched,
  ]);

  const columns = useMemo(() => {
    if (!isTableSettingsFetched || !isCustomFieldsFetched) return [];
    const initialColumns = getCampaignTargetsTableColumns({
      campaignId,
      campaign,
      invalidateData,
      deleteCampaignTarget,
    });
    const campaignTargetsTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${tableName}`,
      {}
    );

    const withCustomFieldsColumns = addCustomFieldsColumns({
      columns: initialColumns,
      customFields,
      visibleColumns: campaignTargetsTableSettings?.visible_columns || [],
    });
    const withResizedColumns = addResizedColumns({
      columns: withCustomFieldsColumns,
      resizedColumns: campaignTargetsTableSettings?.resized_columns || {},
    });

    return withResizedColumns;
  }, [
    isTableSettingsFetched,
    isCustomFieldsFetched,
    campaignId,
    tableName,
    campaign,
    customFields,
    tableSettings,
    invalidateData,
    deleteCampaignTarget,
  ]);

  return {
    filters,
    columns,
    tags,
    customFields,
    queryKey,
    invalidateData,
    deleteManyCampaignTarget,
  };
};
